import { Check, Sprout, Globe2, GraduationCap, Leaf, Wheat, Droplets, TreePine, Fish, Grape, ShieldCheck, HandHeart } from "lucide-react";
import { PageHero, CTABand } from "../components/sections";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { EnquiryForm } from "../components/EnquiryForm";
import { IMAGES } from "../data/site";

const PROGRAMS = [
  { icon: Leaf, t: "Agriculture, Dairy & Wineries" },
  { icon: Grape, t: "Horticulture & Nurseries" },
  { icon: TreePine, t: "Forest Management" },
  { icon: Sprout, t: "Environmental Education" },
  { icon: Fish, t: "Aquaculture" },
  { icon: Wheat, t: "Others on Request" },
];

const DESTINATIONS = [
  { place: "Israel", note: "Where it all began — desert agriculture & drip irrigation" },
  { place: "Holland, Norway & Denmark", note: "World-leading dairy production" },
  { place: "Mauritius", note: "Sugarcane production & allied activities" },
  { place: "Punjab, India", note: "Large-scale wheat & grain cultivation" },
  { place: "More on Request", note: "New destinations explored as per your group" },
];

const PROVIDE = [
  "Easy, exciting and safe travel throughout",
  "Scheduled visits based on the group's interests",
  "Cultural exchanges with local communities",
  "Sightseeing — jungle trips, mountains, historic sites",
  "Pre-trip cultural & travel information",
  "Health, money and climate logistics handled",
  "All meals and comfortable lodging",
  "All in-country travel arranged",
  "Arrival orientation to the country & people",
  "Experienced, knowledgeable bilingual guides",
];

const WHY_US = [
  "Strong in-country networks & local relationships",
  "Small or large intimate group sizes",
  "Personal attention from start to finish",
  "Local bilingual guides in every country",
  "Flexible programs customised to your interests",
  "Programs that respect & protect the environment",
  "Emphasis on supporting local communities",
  "Cultural activities woven into every tour",
  "A genuine opportunity to learn and grow",
];

export default function FarmersTours() {
  return (
    <div>
      <PageHero eyebrow="Government-Approved Study Tours" title="Farmers' Study Tours" subtitle="Since 2018, Life Line has taken Indian farmers to the world's agricultural innovation capitals — starting with Israel." image={IMAGES.farm} />

      <section className="ll-container ll-section grid md:grid-cols-2 gap-14 items-center">
        <Reveal>
          <p className="eyebrow text-[#f7ad2d]">Agricultural Study Tours</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold mt-4 leading-tight">Learning from the best farmers on earth</h2>
          <p className="text-[#4A5D58] text-lg mt-6 leading-relaxed">
            Our specialty lies in agricultural study tours designed for the Indian market — helping <strong className="text-[#0A221C]">Indian farmers</strong> improve production and financial growth through world-class exposure. Small, specialised expeditions take you off the beaten track, each one customised to your group's interests.
          </p>
          <p className="text-[#4A5D58] text-lg mt-4 leading-relaxed">
            In 2018, <strong className="text-[#0A221C]">Life Line Tours and Travels Pvt. Ltd.</strong> was awarded a government contract to organise Farmers' Education Tours to Israel. We took around <strong className="text-[#0A221C]">50 farmers along with agricultural officers</strong> — work that was appreciated by the Government of Maharashtra. From here on, we're always there for the farmers of Maharashtra.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <img src={IMAGES.farm} alt="Farmers agricultural study tour" className="rounded-[2rem] w-full h-[480px] object-cover" />
        </Reveal>
      </section>

      {/* Programs */}
      <section className="bg-[#13382F]/5">
        <div className="ll-container ll-section">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <p className="eyebrow text-[#f7ad2d]">Programs Can Include</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-3">Fields we specialise in</h2>
          </Reveal>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PROGRAMS.map((p) => (
              <StaggerItem key={p.t}>
                <div className="ll-card p-8 h-full flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-[#0A221C] flex items-center justify-center shrink-0"><p.icon size={24} className="text-[#4ef72d]" /></div>
                  <h3 className="font-display text-xl font-bold">{p.t}</h3>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Where we travel */}
      <section className="bg-[#0A221C] relative overflow-hidden">
        <div className="grain" />
        <div className="ll-container ll-section relative">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <p className="eyebrow text-[#4ef72d]">Where We Travel</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mt-3">Destinations for every discipline</h2>
          </Reveal>
          <div className="max-w-3xl mx-auto space-y-4">
            {DESTINATIONS.map((d, i) => (
              <Reveal key={d.place} delay={i * 0.06}>
                <div className="flex items-start gap-4 border border-white/10 rounded-2xl p-6 hover:border-[#4ef72d]/50 transition-colors">
                  <Globe2 size={22} className="text-[#4ef72d] shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-semibold text-lg">{d.place}</p>
                    <p className="text-white/60 text-sm mt-0.5">{d.note}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="grid sm:grid-cols-3 gap-6 mt-14 max-w-3xl mx-auto text-center">
            {[{ i: ShieldCheck, t: "Govt.-Approved since 2018" }, { i: Droplets, t: "Israel & beyond" }, { i: GraduationCap, t: "Expert-led sessions" }].map((x, k) => (
              <div key={k} className="text-white/85">
                <x.i size={30} className="text-[#4ef72d] mx-auto mb-3" />
                <p className="font-semibold">{x.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What we provide + Why us */}
      <section className="ll-container ll-section grid md:grid-cols-2 gap-14">
        <Reveal>
          <p className="eyebrow text-[#f7ad2d]">What We Provide</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 mb-7">Everything, taken care of</h2>
          <ul className="space-y-3">
            {PROVIDE.map((p) => (
              <li key={p} className="flex gap-3 text-[#4A5D58]"><Check size={18} className="text-[#4ef72d] shrink-0 mt-1" /> {p}</li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="eyebrow text-[#f7ad2d]">Why Travel With Life Line</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 mb-7">Knowledge meets hard work</h2>
          <ul className="space-y-3">
            {WHY_US.map((p) => (
              <li key={p} className="flex gap-3 text-[#4A5D58]"><HandHeart size={18} className="text-[#f7ad2d] shrink-0 mt-1" /> {p}</li>
            ))}
          </ul>
        </Reveal>
      </section>

      <section className="ll-container pb-8 grid md:grid-cols-2 gap-14 items-center">
        <Reveal>
          <p className="eyebrow text-[#f7ad2d]">Enrol Your Group</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold mt-3 leading-tight">Organise a study tour for your farmers</h2>
          <p className="text-[#4A5D58] text-lg mt-5">For cooperatives, FPOs, agri-institutions, youth leaders, students and government bodies. Share your group details and we&apos;ll design a complete, customised study programme.</p>
        </Reveal>
        <Reveal delay={0.15}><div className="ll-card p-8"><EnquiryForm packageName="Farmers' Study Tour" /></div></Reveal>
      </section>

      <div className="ll-section"><CTABand /></div>
    </div>
  );
}
